import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  packagesApi,
  type CreatePackageTemplatePayload,
  type UpdatePackageTemplatePayload,
  type CreateAddOnPayload,
  type UpdateAddOnPayload,
} from '../api/packagesApi';
import { useToast } from '../../../shared/components/toast/useToast';

const errMsg = (err: unknown, fallback: string) => {
  const msg = (err as { message?: unknown })?.message;
  if (Array.isArray(msg)) return (msg as string[])[0] || fallback;
  return (typeof msg === 'string' ? msg : '') || fallback;
};

const TEMPLATES_KEY = 'package-templates';
const ADDONS_KEY = 'package-addons';

// --- Templates ---
export const usePackageTemplates = (tenantId: string | null | undefined) =>
  useQuery({
    queryKey: [TEMPLATES_KEY, tenantId],
    queryFn: () => packagesApi.listTemplates(),
    enabled: !!tenantId,
    staleTime: 60 * 1000,
  });

export const useCreatePackageTemplate = () => {
  const qc = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: (data: CreatePackageTemplatePayload) => packagesApi.createTemplate(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [TEMPLATES_KEY] });
      toast.success('Package template created');
    },
    onError: (err: unknown) => toast.error(errMsg(err, 'Failed to create package template')),
  });
};

export const useUpdatePackageTemplate = () => {
  const qc = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdatePackageTemplatePayload }) =>
      packagesApi.updateTemplate(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [TEMPLATES_KEY] });
      toast.success('Package template updated');
    },
    onError: (err: unknown) => toast.error(errMsg(err, 'Failed to update package template')),
  });
};

export const useDeletePackageTemplate = () => {
  const qc = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: (id: string) => packagesApi.deleteTemplate(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [TEMPLATES_KEY] });
      qc.invalidateQueries({ queryKey: [ADDONS_KEY] });
      toast.success('Package template deleted');
    },
    onError: (err: unknown) => toast.error(errMsg(err, 'Failed to delete package template')),
  });
};

// --- Add-ons ---
export const useAddOns = (tenantId: string | null | undefined) =>
  useQuery({
    queryKey: [ADDONS_KEY, tenantId],
    queryFn: () => packagesApi.listAddOns(),
    enabled: !!tenantId,
    staleTime: 60 * 1000,
  });

export const useCreateAddOn = () => {
  const qc = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: (data: CreateAddOnPayload) => packagesApi.createAddOn(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [ADDONS_KEY] });
      toast.success('Add-on created');
    },
    onError: (err: unknown) => toast.error(errMsg(err, 'Failed to create add-on')),
  });
};

export const useUpdateAddOn = () => {
  const qc = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateAddOnPayload }) => packagesApi.updateAddOn(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [ADDONS_KEY] });
      toast.success('Add-on updated');
    },
    onError: (err: unknown) => toast.error(errMsg(err, 'Failed to update add-on')),
  });
};

export const useRemoveAddOn = () => {
  const qc = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: (id: string) => packagesApi.removeAddOn(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [ADDONS_KEY] });
      toast.success('Add-on archived');
    },
    onError: (err: unknown) => toast.error(errMsg(err, 'Failed to archive add-on')),
  });
};
